import { useState } from 'react';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStandardsAnalytics, DateRange } from '@/hooks/useStandardsAnalytics';
import { 
  Eye, 
  CheckCircle2, 
  Download, 
  Search,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Activity,
  Users,
  FileCode,
  BookOpen,
  Hammer,
  Minus,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';

type DateRangePreset = 'today' | 'week' | 'month' | 'quarter';

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(210 60% 50%)',
  'hsl(280 60% 50%)',
  'hsl(340 60% 50%)',
];

export function AnalyticsManager() {
  const [preset, setPreset] = useState<DateRangePreset>('week');
  const [activityTab, setActivityTab] = useState('all');
  const [contentTab, setContentTab] = useState('api');

  const getDateRange = (preset: DateRangePreset): DateRange => {
    const now = new Date();
    switch (preset) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case 'week':
        return { from: startOfDay(subDays(now, 7)), to: endOfDay(now) };
      case 'month':
        return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
      case 'quarter':
        return { from: startOfDay(subDays(now, 90)), to: endOfDay(now) };
    }
  };

  const dateRange = getDateRange(preset);
  const {
    isLoading,
    summary,
    comparison,
    topViewedStandards,
    topAdfElements,
    topDocArticles,
    exportsByFormat,
    dailyActivity,
    topSearchTerms,
    apiUsageByEndpoint,
    builderUsage,
  } = useStandardsAnalytics(dateRange);

  const presetLabels: Record<DateRangePreset, string> = {
    today: 'Today',
    week: 'Last 7 Days',
    month: 'Last 30 Days',
    quarter: 'Last 90 Days',
  };

  // Prepare activity data based on selected tab
  const getActivityData = () => {
    switch (activityTab) {
      case 'api':
        return dailyActivity.map(d => ({ date: d.date, value: d.views }));
      case 'adf':
        return dailyActivity.map(d => ({ date: d.date, value: d.adfViews }));
      case 'docs':
        return dailyActivity.map(d => ({ date: d.date, value: d.docViews }));
      default:
        return dailyActivity.map(d => ({ 
          date: d.date, 
          'API Standards': d.views,
          'ADF': d.adfViews,
          'Documentation': d.docViews,
          'Builder': d.builderActions,
        }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Analytics
          </h1>
          <p className="text-muted-foreground">Track usage and adoption metrics across the platform</p>
        </div>
        <Select value={preset} onValueChange={(v) => setPreset(v as DateRangePreset)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(presetLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats - Compact Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard
          title="Views"
          value={summary.totalViews}
          change={comparison.viewsChange}
          icon={Eye}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Validations"
          value={summary.totalValidations}
          change={comparison.validationsChange}
          icon={CheckCircle2}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Exports"
          value={summary.totalExports}
          change={comparison.exportsChange}
          icon={Download}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Searches"
          value={summary.totalSearches}
          change={comparison.searchesChange}
          icon={Search}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Sessions"
          value={summary.uniqueSessions}
          change={comparison.sessionsChange}
          icon={Users}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Builder Actions"
          value={summary.totalBuilderActions}
          icon={Hammer}
          isLoading={isLoading}
        />
      </div>

      {/* Activity Chart with Tabs */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Over Time
              </CardTitle>
              <CardDescription>Views, validations, and exports by section</CardDescription>
            </div>
            <Tabs value={activityTab} onValueChange={setActivityTab}>
              <TabsList className="h-8">
                <TabsTrigger value="all" className="text-xs px-2">All</TabsTrigger>
                <TabsTrigger value="api" className="text-xs px-2">API</TabsTrigger>
                <TabsTrigger value="adf" className="text-xs px-2">ADF</TabsTrigger>
                <TabsTrigger value="docs" className="text-xs px-2">Docs</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : dailyActivity.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              {activityTab === 'all' ? (
                <LineChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))',
                      fontSize: '12px',
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="API Standards" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="ADF" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Documentation" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Builder" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={false} />
                </LineChart>
              ) : (
                <BarChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))',
                      fontSize: '12px',
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No activity data available" />
          )}
        </CardContent>
      </Card>

      {/* Top Content & Exports Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Content with Tabs */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="text-lg">Most Viewed Content</CardTitle>
              <Tabs value={contentTab} onValueChange={setContentTab}>
                <TabsList className="h-8">
                  <TabsTrigger value="api" className="text-xs px-2">
                    <FileCode className="h-3 w-3 mr-1" />
                    API
                  </TabsTrigger>
                  <TabsTrigger value="adf" className="text-xs px-2">
                    <FileCode className="h-3 w-3 mr-1" />
                    ADF
                  </TabsTrigger>
                  <TabsTrigger value="docs" className="text-xs px-2">
                    <BookOpen className="h-3 w-3 mr-1" />
                    Docs
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {contentTab === 'api' && (
                  topViewedStandards.length > 0 ? (
                    topViewedStandards.map((item, i) => (
                      <ContentRow key={i} rank={i + 1} title={item.title} value={item.views} />
                    ))
                  ) : (
                    <EmptyState message="No API standard views yet" />
                  )
                )}
                {contentTab === 'adf' && (
                  topAdfElements.length > 0 ? (
                    topAdfElements.map((item, i) => (
                      <ContentRow key={i} rank={i + 1} title={item.slug} value={item.views} />
                    ))
                  ) : (
                    <EmptyState message="No ADF element views yet" />
                  )
                )}
                {contentTab === 'docs' && (
                  topDocArticles.length > 0 ? (
                    topDocArticles.map((item, i) => (
                      <ContentRow 
                        key={i} 
                        rank={i + 1} 
                        title={item.slug} 
                        value={item.views} 
                        subtitle={item.docType}
                      />
                    ))
                  ) : (
                    <EmptyState message="No documentation views yet" />
                  )
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Formats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Export Formats</CardTitle>
            <CardDescription>Distribution by format type</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : exportsByFormat.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={exportsByFormat}
                    dataKey="count"
                    nameKey="format"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ format, percent }) => `${format} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {exportsByFormat.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))' 
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No export data available" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Engagement Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Search Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="h-5 w-5" />
              Top Search Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : topSearchTerms.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {topSearchTerms.slice(0, 10).map((item, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded"
                  >
                    <code className="text-sm truncate flex-1">{item.term}</code>
                    <span className="text-sm font-medium text-muted-foreground ml-4">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No search data available" />
            )}
          </CardContent>
        </Card>

        {/* Builder Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Hammer className="h-5 w-5" />
              Builder Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : builderUsage.length > 0 ? (
              <div className="space-y-3">
                {builderUsage.map((item, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        <Hammer className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">{item.builder}</span>
                    </div>
                    <span className="text-lg font-bold">{item.actions}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState message="No builder usage data available" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* API Usage */}
      {apiUsageByEndpoint.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              API Usage by Endpoint
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {apiUsageByEndpoint.map((item, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded"
                >
                  <code className="text-sm truncate flex-1">{item.endpoint}</code>
                  <div className="flex items-center gap-4 ml-4">
                    <span className="text-sm font-medium">{item.count} calls</span>
                    <span className="text-xs text-muted-foreground">
                      {item.successRate}% success
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: number;
  change?: number;
  icon: React.ElementType;
  isLoading: boolean;
}

function SummaryCard({ title, value, change, icon: Icon, isLoading }: SummaryCardProps) {
  const getTrendIcon = () => {
    if (change === undefined || change === 0) return <Minus className="h-3 w-3 text-muted-foreground" />;
    if (change > 0) return <TrendingUp className="h-3 w-3 text-green-500" />;
    return <TrendingDown className="h-3 w-3 text-red-500" />;
  };

  const getTrendColor = () => {
    if (change === undefined || change === 0) return 'text-muted-foreground';
    if (change > 0) return 'text-green-500';
    return 'text-red-500';
  };

  return (
    <Card className="p-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <>
              <Skeleton className="h-6 w-12 mb-1" />
              <Skeleton className="h-3 w-16" />
            </>
          ) : (
            <>
              <div className="text-xl font-bold">{value.toLocaleString()}</div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground truncate">{title}</span>
                {change !== undefined && (
                  <div className={`flex items-center gap-0.5 ${getTrendColor()}`}>
                    {getTrendIcon()}
                    <span className="text-xs">{Math.abs(change)}%</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </Card>
  );
}

interface ContentRowProps {
  rank: number;
  title: string;
  value: number;
  subtitle?: string;
}

function ContentRow({ rank, title, value, subtitle }: ContentRowProps) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 bg-muted/50 rounded">
      <span className="text-xs font-medium text-muted-foreground w-5">{rank}.</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
      </div>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
      {message}
    </div>
  );
}

export default AnalyticsManager;
